import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../../services/api';
import { getFileUrl } from '../../utils/fileUtils';
import AdminNavbar from '../../components/admin/AdminNavbar';
import { FaDollarSign, FaCheckCircle, FaTimesCircle, FaClock, FaCog } from 'react-icons/fa';
import './AdminDonaciones.css';

const AdminDonaciones = () => {
  const [filtroEstado, setFiltroEstado] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const queryClient = useQueryClient();
  const [donacionForm, setDonacionForm] = useState({
    donaciones_campana_titulo: '',
    donaciones_campana_descripcion: '',
    donaciones_campana_imagen_url: '',
    donaciones_banco: '',
    donaciones_cuenta: '',
    donaciones_tipo_cuenta: '',
    donaciones_titular: '',
  });
  const [donacionImagenFile, setDonacionImagenFile] = useState(null);

  const { data: donaciones = [], isLoading } = useQuery({
    queryKey: ['donaciones-admin', filtroEstado, filtroTipo],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await api.get('/donaciones', { 
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    }
  });

  const { data: estadisticas } = useQuery({
    queryKey: ['donaciones-estadisticas'],
    queryFn: async () => {
      const token = localStorage.getItem('token');
      const response = await api.get('/donaciones/estadisticas/resumen', { 
        headers: { Authorization: `Bearer ${token}` }
      });
      return response.data;
    }
  });

  const { data: config = {} } = useQuery({
    queryKey: ['configuracion'],
    queryFn: async () => {
      const response = await api.get('/configuracion');
      return response.data;
    }
  });

  useEffect(() => {
    setDonacionForm({
      donaciones_campana_titulo: config.donaciones_campana_titulo || '',
      donaciones_campana_descripcion: config.donaciones_campana_descripcion || '',
      donaciones_campana_imagen_url: config.donaciones_campana_imagen || '',
      donaciones_banco: config.donaciones_banco || '',
      donaciones_cuenta: config.donaciones_cuenta || '',
      donaciones_tipo_cuenta: config.donaciones_tipo_cuenta || '',
      donaciones_titular: config.donaciones_titular || ''
    });
  }, [config]);

  const handleDonacionSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (donacionImagenFile) {
        const formData = new FormData();
        formData.append('imagen', donacionImagenFile);
        await api.put('/configuracion/donaciones_campana_imagen', formData, {
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
        });
      }
      const campos = ['donaciones_campana_titulo', 'donaciones_campana_descripcion', 'donaciones_banco', 'donaciones_cuenta', 'donaciones_tipo_cuenta', 'donaciones_titular'];
      for (const clave of campos) {
        const valor = donacionForm[clave] || '';
        if (valor !== (config[clave] || '')) {
          await api.put(`/configuracion/${clave}`, { valor }, { headers: { Authorization: `Bearer ${token}` } });
        }
      }
      queryClient.invalidateQueries(['configuracion']);
      setDonacionImagenFile(null);
      const fileInput = document.querySelector('input[name="donacion_imagen"]');
      if (fileInput) fileInput.value = '';
      alert('Campaña de donaciones actualizada exitosamente');
    } catch (error) {
      alert('Error al actualizar campaña de donaciones: ' + (error.response?.data?.error || error.message));
    }
  };

  const updateMutation = useMutation({
    mutationFn: async ({ id, estado, recibo_generado }) => {
      const token = localStorage.getItem('token');
      const response = await api.put(`/donaciones/${id}`, 
        { estado, recibo_generado },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['donaciones-admin']);
      queryClient.invalidateQueries(['donaciones-estadisticas']);
    }
  });

  const handleEstadoChange = (id, nuevoEstado, reciboGenerado = false) => {
    updateMutation.mutate({ id, estado: nuevoEstado, recibo_generado: reciboGenerado });
  };

  const estados = [
    { value: '', label: 'Todos' },
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'procesando', label: 'Procesando' },
    { value: 'completada', label: 'Completada' },
    { value: 'fallida', label: 'Fallida' },
    { value: 'cancelada', label: 'Cancelada' }
  ];

  const tipos = [
    { value: '', label: 'Todos' },
    { value: 'diezmo', label: 'Diezmo' },
    { value: 'donacion_unica', label: 'Donación Única' },
    { value: 'donacion_recurrente', label: 'Donación Recurrente' },
    { value: 'obra_social', label: 'Obra Social' },
    { value: 'seminario', label: 'Seminario' },
    { value: 'mantenimiento', label: 'Mantenimiento' }
  ];

  const filteredDonaciones = donaciones.filter(d => {
    const matchEstado = !filtroEstado || d.estado === filtroEstado;
    const matchTipo = !filtroTipo || d.tipo_donacion === filtroTipo;
    return matchEstado && matchTipo;
  });

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="admin-donaciones">
      <AdminNavbar />
      <div className="admin-content">
        <div className="admin-header">
          <h1><FaDollarSign /> Gestión de Donaciones</h1>
        </div>

        {/* Configuración de la campaña - visible y accesible */}
        <div className="admin-donaciones-campana-config">
          <h2><FaCog /> Configurar campaña actual</h2>
          <p className="campana-config-desc">Imagen, título, descripción y datos bancarios. El botón del sitio siempre muestra "Dona".</p>
          <form onSubmit={handleDonacionSubmit} className="donaciones-campana-form compact">
            <div className="campana-form-row">
              <div className="form-group">
                <label>Título de la campaña</label>
                <input
                  type="text"
                  value={donacionForm.donaciones_campana_titulo}
                  onChange={(e) => setDonacionForm({ ...donacionForm, donaciones_campana_titulo: e.target.value })}
                  placeholder="Ej: Dona a nuestros niños"
                />
              </div>
              <div className="form-group">
                <label>Imagen</label>
                <input
                  name="donacion_imagen"
                  type="file"
                  accept="image/*"
                  onChange={(e) => setDonacionImagenFile(e.target.files[0])}
                />
                {donacionForm.donaciones_campana_imagen_url && (
                  <div className="donacion-imagen-preview">
                    <img src={getFileUrl(donacionForm.donaciones_campana_imagen_url)} alt="Campaña actual" />
                  </div>
                )}
              </div>
            </div>
            <div className="form-group">
              <label>Descripción</label>
              <textarea
                value={donacionForm.donaciones_campana_descripcion}
                onChange={(e) => setDonacionForm({ ...donacionForm, donaciones_campana_descripcion: e.target.value })}
                placeholder="Texto adicional de la campaña..."
                rows="2"
              />
            </div>
            <div className="campana-form-row">
              <div className="form-group">
                <label>Banco</label>
                <input
                  type="text"
                  value={donacionForm.donaciones_banco}
                  onChange={(e) => setDonacionForm({ ...donacionForm, donaciones_banco: e.target.value })}
                  placeholder="Ej: BANCOLOMBIA"
                />
              </div>
              <div className="form-group">
                <label>Cuenta</label>
                <input
                  type="text"
                  value={donacionForm.donaciones_cuenta}
                  onChange={(e) => setDonacionForm({ ...donacionForm, donaciones_cuenta: e.target.value })}
                  placeholder="Ej: 88800010489"
                />
              </div>
              <div className="form-group">
                <label>Tipo cuenta</label>
                <input
                  type="text"
                  value={donacionForm.donaciones_tipo_cuenta}
                  onChange={(e) => setDonacionForm({ ...donacionForm, donaciones_tipo_cuenta: e.target.value })}
                  placeholder="Ej: Corriente"
                />
              </div>
              <div className="form-group">
                <label>Titular</label>
                <input
                  type="text"
                  value={donacionForm.donaciones_titular}
                  onChange={(e) => setDonacionForm({ ...donacionForm, donaciones_titular: e.target.value })}
                  placeholder="Ej: Diócesis de Ipiales"
                />
              </div>
            </div>
            <button type="submit" className="btn btn-primary">
              Guardar campaña
            </button>
          </form>
        </div>

        {estadisticas && (
          <div className="estadisticas-donaciones">
            <div className="stat-card destacado">
              <h3>Total Recaudado</h3>
              <p>{formatCurrency(estadisticas.total_recaudado || 0)}</p>
            </div>
            <div className="stat-card">
              <h3>Total Donaciones</h3>
              <p>{estadisticas.total_donaciones || 0}</p>
            </div>
            <div className="stat-card">
              <h3>Diezmo</h3>
              <p>{formatCurrency(estadisticas.total_diezmo || 0)}</p>
            </div>
            <div className="stat-card">
              <h3>Promedio</h3>
              <p>{formatCurrency(estadisticas.promedio_donacion || 0)}</p>
            </div>
          </div>
        )}

        <div className="filtros">
          <select
            value={filtroEstado}
            onChange={(e) => setFiltroEstado(e.target.value)}
            className="filtro-select"
          >
            {estados.map(estado => (
              <option key={estado.value} value={estado.value}>{estado.label}</option>
            ))}
          </select>
          <select
            value={filtroTipo}
            onChange={(e) => setFiltroTipo(e.target.value)}
            className="filtro-select"
          >
            {tipos.map(tipo => (
              <option key={tipo.value} value={tipo.value}>{tipo.label}</option>
            ))}
          </select>
        </div>

        {isLoading ? (
          <div className="loading">Cargando donaciones...</div>
        ) : filteredDonaciones.length === 0 ? (
          <div className="no-results">No hay donaciones registradas.</div>
        ) : (
          <div className="donaciones-table-container">
            <table className="donaciones-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Donante</th>
                  <th>Monto</th>
                  <th>Tipo</th>
                  <th>Destino</th>
                  <th>Fecha</th>
                  <th>Estado</th>
                  <th>Recibo</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {filteredDonaciones.map(donacion => (
                  <tr key={donacion.id}>
                    <td>{donacion.id}</td>
                    <td>
                      <div>
                        <strong>{donacion.donante_nombre}</strong>
                        {donacion.donante_email && <div className="text-muted">{donacion.donante_email}</div>}
                        {donacion.donante_telefono && <div className="text-muted">{donacion.donante_telefono}</div>}
                      </div>
                    </td>
                    <td className="monto-cell">
                      <strong>{formatCurrency(donacion.monto)}</strong>
                    </td>
                    <td>
                      <span className="badge badge-tipo">{donacion.tipo_donacion}</span>
                    </td>
                    <td>{donacion.destino || '-'}</td>
                    <td>{new Date(donacion.fecha_donacion || donacion.created_at).toLocaleDateString('es-CO')}</td>
                    <td>
                      <span className={`estado-badge ${donacion.estado}`}>
                        {donacion.estado}
                      </span>
                    </td>
                    <td>
                      {donacion.recibo_generado ? (
                        <FaCheckCircle className="recibo-icon" />
                      ) : (
                        <span className="text-muted">No</span>
                      )}
                    </td>
                    <td>
                      <select
                        value={donacion.estado}
                        onChange={(e) => handleEstadoChange(donacion.id, e.target.value, donacion.recibo_generado)}
                        className="estado-select"
                      >
                        <option value="pendiente">Pendiente</option>
                        <option value="procesando">Procesando</option>
                        <option value="completada">Completada</option>
                        <option value="fallida">Fallida</option>
                        <option value="cancelada">Cancelada</option>
                      </select>
                      {donacion.estado === 'completada' && !donacion.recibo_generado && (
                        <button
                          onClick={() => handleEstadoChange(donacion.id, 'completada', true)}
                          className="btn btn-sm btn-success"
                          style={{ marginTop: '0.5rem' }}
                        >
                          Generar Recibo
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDonaciones;
